import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import TranslationDeleteAction from "@/v2-backoffice-client/translation/orphaned/TranslationDeleteAction"
import { useTranslationOrphanedQuery } from "@/v2-backoffice-client/translation/orphaned/translation.orphaned.query"

// A UI text that exists in the database but not in the code anymore.
type TranslationOrphanedRow = {
  code: string
  name: string
  description: string
}

const ORPHANED_COLUMNS = createColumns<TranslationOrphanedRow>({
  enableSelection: true,
  enableSorting: true,
  columns: {
    code: { id: "code", label: "Code" },
    name: { id: "name", label: "English (database)" },
    description: { id: "description", label: "Description" },
  },
})

export type TranslationOrphanedTableProps = Omit<
  TableProps<TranslationOrphanedRow>,
  "data" | "columns"
>

// "Missing in code": UI-component texts that exist in the database but have
// no matching *.text.ts entry - their code usage was removed or renamed.
// Select rows to soft-delete them. Contextual help lives in the sidebar
// item's info tooltip. The mirror image is TranslationMissingTable.
function TranslationOrphanedTable(props: TranslationOrphanedTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error } = useTranslationOrphanedQuery()

  errorToast(error, { title: "Failed to load texts missing in code" })

  const rows: TranslationOrphanedRow[] = (
    data?.getFeaturesMissingCodeTexts ?? []
  ).map((feature) => ({
    code: feature.Code,
    name: feature.Name ?? "",
    description: feature.Description ?? "",
  }))

  return (
    <Table
      className={cn("translation-orphaned-table", className)}
      tableId="translation-orphaned-table"
      data={rows}
      columns={ORPHANED_COLUMNS}
      getRowId={(row) => row.code}
      isCellsWrapped
      enableInfiniteScroll
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search code or text..." />
        <div className="flex-1" />
        <Table.ToolbarMenu exportFileName="missing-in-code" />
      </Table.Toolbar>

      <Table.Content isLoading={loading} error={error} />

      <Table.SelectionToolbar<TranslationOrphanedRow>>
        {(selected, reset) => (
          <TranslationDeleteAction selectedRows={selected} onComplete={reset} />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TranslationOrphanedTable
